/*!
 * Copyright (c) 2026 Elevation Technologies Pte Ltd.
 * Developer: filimoni@elevationtech.com.fj
 */
import React, { useState } from 'react';
import PhotoLightbox from '../components/PhotoLightbox';
import VideoFrame from '../components/VideoFrame';
import { QUALITY_IMAGES, QUALITY_PHOTOS } from '../data/siteData';

export function QualityCopy({ html, className = '' }) {
  if (!html) {
    return null;
  }

  return <div className={`fsf-quality-copy ${className}`.trim()} dangerouslySetInnerHTML={{ __html: html }} />;
}

export function QualityMedia({ photo, index, onOpen, className = '', loading = 'lazy' }) {
  if (!photo?.src) {
    return null;
  }

  return (
    <figure className={`fsf-quality-media ${className}`.trim()}>
      <button
        className="fsf-quality-media__button"
        type="button"
        onClick={() => onOpen(index)}
        aria-label={`Open ${photo.alt}`}
      >
        <img src={photo.src} alt={photo.alt} loading={loading} />
      </button>
    </figure>
  );
}

export default function QualityPageSections({ page }) {
  const [activePhotoIndex, setActivePhotoIndex] = useState(null);
  const copyBlocks = (page.blocks || []).filter((block) => block?.type === 'html');
  const videoBlocks = (page.blocks || []).filter((block) => block?.type === 'video');

  return (
    <>
      <div className="fsf-quality-story">
        <section className="fsf-quality-section fsf-quality-section--intro">
          <QualityCopy html={copyBlocks[0]?.html} className="fsf-quality-copy--intro" />

          <div className="fsf-quality-videos" aria-label="Freight Services Fiji quality videos">
            {videoBlocks.map((block, index) => (
              <VideoFrame key={block.url} url={block.url} title={`Freight Services Fiji quality video ${index + 1}`} />
            ))}
          </div>
        </section>

        <section className="fsf-quality-section">
          <QualityCopy html={copyBlocks[1]?.html} className="fsf-quality-copy--industry" />
        </section>

        <section className="fsf-quality-section fsf-quality-section--split">
          <QualityMedia
            photo={QUALITY_IMAGES.bsiCertificate}
            index={0}
            onOpen={setActivePhotoIndex}
            className="fsf-quality-media--certificate"
            loading="eager"
          />
          <QualityCopy html={copyBlocks[2]?.html} />
        </section>

        <section className="fsf-quality-section fsf-quality-section--logo">
          <QualityMedia
            photo={QUALITY_IMAGES.bsiMark}
            index={1}
            onOpen={setActivePhotoIndex}
            className="fsf-quality-media--logo"
          />
          <QualityCopy html={copyBlocks[3]?.html} />
        </section>

        <section className="fsf-quality-section fsf-quality-section--feature">
          <QualityMedia
            photo={QUALITY_IMAGES.bsiTeam}
            index={2}
            onOpen={setActivePhotoIndex}
            className="fsf-quality-media--wide"
          />
          <QualityCopy html={copyBlocks[4]?.html} />
        </section>

        <section className="fsf-quality-section fsf-quality-section--split">
          <QualityMedia
            photo={QUALITY_IMAGES.faimCertificate}
            index={3}
            onOpen={setActivePhotoIndex}
            className="fsf-quality-media--certificate"
          />
          <QualityCopy html={copyBlocks[5]?.html} />
        </section>

        <section className="fsf-quality-section fsf-quality-section--feature">
          <QualityMedia
            photo={QUALITY_IMAGES.faimTeam}
            index={4}
            onOpen={setActivePhotoIndex}
            className="fsf-quality-media--tall"
          />
          <QualityCopy html={copyBlocks[6]?.html} />
        </section>

        <section className="fsf-quality-section fsf-quality-section--cornerstone">
          <QualityMedia
            photo={QUALITY_IMAGES.cornerstone}
            index={5}
            onOpen={setActivePhotoIndex}
            className="fsf-quality-media--portrait"
          />
          <QualityCopy html={copyBlocks[7]?.html} className="fsf-quality-copy--cornerstone" />
        </section>
      </div>

      <PhotoLightbox
        images={QUALITY_PHOTOS}
        activeIndex={activePhotoIndex}
        setActiveIndex={setActivePhotoIndex}
        label="Freight Services Fiji quality assurance photos"
      />
    </>
  );
}
